import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { gsap } from 'gsap';
import barba from '@barba/core';
import { Postcard } from './Postcard';
import { MapPopover } from './MapPopover';
import { MapCursor } from './MapCursor';

const cities = window.map_cities;
const map_model_url = `${window.theme_assets_url}/models/map.glb?v1.1`;

export class InteractiveMap {
    constructor() {
        this.zoomLevels = {
            overview: 20,
            city: 25
        };

        this.mapWidth = 1186;
        this.mapHeight = 565;

        this.panLimits = {
            overview: 0.55,
            city: 0.75
        };

        this.container = document.getElementById('map-container');

        // Initialize cursor
        this.cursor = new MapCursor(this.container);


        // Add pan indicator with cached dimensions
        this.panIndicator = document.createElement('div');
        this.panIndicator.className = 'map-pan-indicator';
        this.panIndicatorTrack = document.createElement('div');
        this.panIndicatorTrack.className = 'map-pan-indicator-track';
        this.panIndicatorThumb = document.createElement('div');
        this.panIndicatorThumb.className = 'map-pan-indicator-thumb';
        this.panIndicatorTrack.appendChild(this.panIndicatorThumb);
        this.panIndicator.appendChild(this.panIndicatorTrack);
        this.container.appendChild(this.panIndicator);

        this.isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

        if (!this.isTouchDevice) {
            this.panIndicator.style.display = 'none';

            this.cursor.onStateChange = (isActive) => {
                // Show custom cursor and hide native cursor when active
                this.container.style.cursor = isActive ? 'none' : 'default';
            };
        } else {
            this.container.style.cursor = 'default';
        }

        this.indicatorDimensions = {
            trackWidth: 0,
            thumbWidth: 0,
            thumbPercentage: 0,
            maxTravel: 0
        };

        this.indicatorRAF = null;
        this.pendingIndicatorUpdate = false;

        setTimeout(() => this.cacheIndicatorDimensions(), 100);

        this.sizes = {
            container: {
                width: this.container.offsetWidth,
                height: this.container.offsetHeight,
            }
        }

        this.sizes.container.aspectRatio = this.sizes.container.width / this.sizes.container.height;

        this.isDragging = false;
        this.dragStart = { x: 0, y: 0 };
        this.dragOffset = { x: 0 };
        this.lastDragPosition = { x: 0 };
        this.velocity = { x: 0 };
        this.lastTouchTime = 0;
        this.lastTouchPosition = { x: 0 };
        this.inertiaAnimationId = null;
        this.touchMoveDistance = 0;
        this.touchStartTime = 0;
        this.dragSensitivityFactor = 0.1;
        this.isHorizontalDrag = null;

        // Add background elements references
        this.overviewBackground = document.querySelector('.gom-hero .map-background.overview');
        this.detailsBackground = document.querySelector('.gom-hero .map-background.details');

        this.scene = new THREE.Scene();
        this.camera = new THREE.OrthographicCamera();
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true
        });

        this.mapGroup = new THREE.Group();
        this.cityMeshes = new Map();

        // Add raycaster for click detection
        this.raycaster = new THREE.Raycaster();
        this.raycaster.layers.enable(0);
        this.mouse = new THREE.Vector2();

        this.textureLoader = new THREE.TextureLoader();
        this.postcards = new Map();

        this.initialState = this.getInitialState();
        this.currentlyHoveredPostcard = null;

        this.selectedPostcard = null;

        // Replace popover related properties
        this.popover = new MapPopover(this.container);

        // Move postcardMeshes initialization here
        this.collisionMeshes = [];

        this.debug = false;

        this.init();
    }

    async init() {
        this.setupRenderer();
        this.setupCamera();
        this.scene.add(this.mapGroup);

        await this.loadMap();
        this.setupEventListeners();
        this.animate();

        // Set initial background state
        if (this.initialState.type === 'city') {
            this.setBackgroundState('details');
        } else {
            this.setBackgroundState('overview');
        }
    }

    setupRenderer() {
        this.renderer.setSize(this.sizes.container.width, this.sizes.container.height);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.container.appendChild(this.renderer.domElement);
    }

    setupCamera() {
        const aspect = this.sizes.container.aspectRatio;
        const frustumSize = Math.max(this.mapWidth, this.mapHeight);

        this.camera = new THREE.OrthographicCamera(
            frustumSize * aspect / -2,
            frustumSize * aspect / 2,
            frustumSize / 2,
            frustumSize / -2,
            -1000,
            1000
        );

        this.camera.position.set(0, 0, 5);
        this.camera.lookAt(0, 0, 0);
    }

    async loadMap() {
        try {
            const loader = new GLTFLoader();
            const gltf = await loader.loadAsync(map_model_url);

            this.mapGroup.position.set(0, 0, 0);

            // Set initial scale based on whether we're loading directly to a city
            if (this.initialState.type === 'city') {
                this.mapGroup.scale.set(
                    this.zoomLevels.overview,
                    this.zoomLevels.overview,
                    this.zoomLevels.overview
                );
            } else {
                this.mapGroup.scale.set(0, 0, 0);
                gsap.to(this.mapGroup.scale, {
                    x: this.zoomLevels.overview,
                    y: this.zoomLevels.overview,
                    z: this.zoomLevels.overview,
                    duration: 1.5,
                    ease: "power2.out",
                    onComplete: () => {
                        this.animatePostcardsAppearance();
                    }
                });
            }

            const map = gltf.scene;

            const meshBackgroundMaterial = new THREE.MeshBasicMaterial({
                color: new THREE.Color('#FFF7F0'),
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 1
            });

            const meshInvisibleMaterial = new THREE.MeshBasicMaterial({
                visible: false
            });

            map.traverse((child) => {
                if (child.isMesh) {
                    if(!child.name.includes('Mesh')) {
                        const cityData = cities.find(city => city.id === child.name);
                        child.material = meshInvisibleMaterial;
                        if (cityData) {
                            // Create helper mesh (invisible but used for positioning)
                            child.userData.cityData = cityData;
                            this.cityMeshes.set(cityData.id, child);


                            // Create and position postcard
                            const postcard = new Postcard(cityData, this.textureLoader);
                            postcard.setPosition(child.position.x, child.position.y);
                            this.mapGroup.add(postcard.group);
                            this.postcards.set(cityData.id, postcard);
                            // Add collision mesh to the array instead of visible mesh
                            this.collisionMeshes.push(postcard.collisionMesh);
                        }
                    } else {
                        // Map background
                        child.material = meshBackgroundMaterial;
                    }
                }
            });

            this.mapGroup.add(map);

            // If loading directly to a city page, zoom to city after a short delay
            if (this.initialState.type === 'city') {
                const cityData = cities.find(city => city.id === this.initialState.cityId);
                if (cityData) {
                    // Short delay to ensure everything is ready
                    setTimeout(() => {
                        this.zoomToCity(cityData);
                        this.animatePostcardsAppearance();
                    }, 100);
                }
            }

        } catch (error) {
            console.error('Error loading map GLB:', error);
        }
    }

    zoomToCity(cityData) {
        const cityMesh = this.cityMeshes.get(cityData.id);
        if (!cityMesh) return;

        // Get local position of the city mesh
        const localPosition = cityMesh.position.clone();

        // Calculate panning
        const maxPanPercentageX = 0.03;
        const maxPanPercentageY = 0.02;
        const normalizedX = localPosition.x / (this.mapWidth / 2);
        const normalizedY = localPosition.y / (this.mapHeight / 2);

        // Only apply pan limits on desktop
        const isMobile = window.innerWidth < 768;
        const panLimitFactorX = isMobile ? 1 : Math.min(Math.abs(normalizedX), maxPanPercentageX) / Math.abs(normalizedX);
        const panLimitFactorY = isMobile ? 1 : Math.min(Math.abs(normalizedY), maxPanPercentageY) / Math.abs(normalizedY);

        let targetX = -localPosition.x * this.zoomLevels.city * (Math.abs(normalizedX) > maxPanPercentageX ? panLimitFactorX : 1);
        let targetY = -localPosition.y * this.zoomLevels.city * (Math.abs(normalizedY) > maxPanPercentageY ? panLimitFactorY : 1);

        // Update postcards state
        const selectedPostcard = this.postcards.get(cityData.id);

        // Create timeline for synchronized animations
        const timeline = gsap.timeline({
            onStart: () => {
                this.setBackgroundState('details');
            }
        });

        // If there's an existing popover, remove it first
        timeline.add(this.popover.remove());

        // Unhover previously selected postcard if exists
        if (this.selectedPostcard && this.selectedPostcard !== selectedPostcard) {
            this.selectedPostcard.unhover();
        }

        // Set new selected postcard
        this.selectedPostcard = selectedPostcard;
        selectedPostcard.hover();

        // Add map animations
        timeline.to(this.mapGroup.scale, {
            x: this.zoomLevels.city,
            y: this.zoomLevels.city,
            z: this.zoomLevels.city,
            duration: 1.5,
            ease: "power2.inOut",
            onUpdate: () => {
                this.scheduleIndicatorUpdate();
            }
        }, ">-0.2");

        timeline.to(this.mapGroup.position, {
            x: targetX,
            y: targetY,
            duration: 1.5,
            ease: "power2.inOut",
            onUpdate: () => {
                this.scheduleIndicatorUpdate();
            }
        }, "<");

        // Show new popover after animations complete
        timeline.call(() => {
            const cardWorldPosition = new THREE.Vector3();
            selectedPostcard.group.getWorldPosition(cardWorldPosition);

            // Convert world position to screen coordinates
            const screenPosition = cardWorldPosition.clone();
            screenPosition.project(this.camera);

            const x = (screenPosition.x * 0.5 + 0.5) * this.container.offsetWidth;
            const y = (-screenPosition.y * 0.5 + 0.5) * this.container.offsetHeight;

            // Calculate rotation (opposite of card rotation)
            const rotation = -parseFloat(cityData.postCardRotate);

            this.popover.create(cityData, { x, y }, rotation);
        });
    }

    resetView() {
        // Reset map group scale
        gsap.to(this.mapGroup.scale, {
            x: this.zoomLevels.overview,
            y: this.zoomLevels.overview,
            z: this.zoomLevels.overview,
            duration: 1.5,
            ease: "power2.inOut"
        });

        // Reset map group position
        gsap.to(this.mapGroup.position, {
            x: 0,
            y: 0,
            duration: 1.5,
            ease: "power2.inOut",
            onStart: () => {
                this.setBackgroundState('overview');
            },
            onUpdate: () => {
                this.scheduleIndicatorUpdate();
            }
        });

        // Reset all postcards to normal state
        this.postcards.forEach(postcard => {
            postcard.unhover();
        });

        // Remove popover
        this.popover.remove();

        this.selectedPostcard = null;
    }

    // Add new method to handle background state
    setBackgroundState(state) {
        if (state === 'overview') {
            this.overviewBackground?.classList.add('visible');
            this.detailsBackground?.classList.remove('visible');
        } else if (state === 'details') {
            this.overviewBackground?.classList.remove('visible');
            this.detailsBackground?.classList.add('visible');
        }
    }

    setupEventListeners() {
        window.addEventListener('resize', this.onWindowResize.bind(this));
        this.container.addEventListener('click', this.onCityClick.bind(this));
        this.container.addEventListener('mousemove', this.onMouseMove.bind(this));

        // Add touch event listeners for mobile drag
        this.container.addEventListener('touchstart', this.onTouchStart.bind(this));
        this.container.addEventListener('touchmove', this.onTouchMove.bind(this));
        this.container.addEventListener('touchend', this.onTouchEnd.bind(this));
    }

    onWindowResize() {
        const newWidth = this.container.offsetWidth;
        const newHeight = this.container.offsetHeight;

        // Only update if dimensions have changed
        if (newWidth !== this.sizes.container.width || newHeight !== this.sizes.container.height) {
            this.sizes.container.width = newWidth;
            this.sizes.container.height = newHeight;
            this.sizes.container.aspectRatio = newWidth / newHeight;

            const aspect = this.sizes.container.aspectRatio;
            const frustumSize = Math.max(this.mapWidth, this.mapHeight);

            this.camera.left = frustumSize * aspect / -2;
            this.camera.right = frustumSize * aspect / 2;
            this.camera.top = frustumSize / 2;
            this.camera.bottom = frustumSize / -2;

            this.camera.updateProjectionMatrix();
            this.renderer.setSize(this.sizes.container.width, this.sizes.container.height);

            // Update pan position if we have a selected postcard
            if (this.selectedPostcard) {
                const cityMesh = this.cityMeshes.get(this.selectedPostcard.cityData.id);
                const localPosition = cityMesh.position.clone();

                const maxPanPercentageX = 0.03;
                const maxPanPercentageY = 0.02;
                const normalizedX = localPosition.x / (this.mapWidth / 2);
                const normalizedY = localPosition.y / (this.mapHeight / 2);

                // Only apply pan limits on desktop
                const isMobile = window.innerWidth < 768;
                const panLimitFactorX = isMobile ? 1 : Math.min(Math.abs(normalizedX), maxPanPercentageX) / Math.abs(normalizedX);
                const panLimitFactorY = isMobile ? 1 : Math.min(Math.abs(normalizedY), maxPanPercentageY) / Math.abs(normalizedY);

                const targetX = -localPosition.x * this.zoomLevels.city * (Math.abs(normalizedX) > maxPanPercentageX ? panLimitFactorX : 1);
                const targetY = -localPosition.y * this.zoomLevels.city * (Math.abs(normalizedY) > maxPanPercentageY ? panLimitFactorY : 1);

                gsap.to(this.mapGroup.position, {
                    x: targetX,
                    y: targetY,
                    duration: 0.3,
                    ease: "power2.out",
                    onComplete: () => {
                        // Update popover position after pan animation
                        const cardWorldPosition = new THREE.Vector3();
                        this.selectedPostcard.group.getWorldPosition(cardWorldPosition);

                        // Convert world position to screen coordinates
                        const screenPosition = cardWorldPosition.clone();
                        screenPosition.project(this.camera);

                        const x = (screenPosition.x * 0.5 + 0.5) * this.container.offsetWidth;
                        const y = (-screenPosition.y * 0.5 + 0.5) * this.container.offsetHeight;

                        // Calculate rotation (opposite of card rotation)
                        const rotation = -parseFloat(this.selectedPostcard.cityData.postCardRotate);

                        this.popover.updatePosition({ x, y }, rotation);
                    }
                });
            }
        }

        this.cacheIndicatorDimensions();

        this.cursor.onResize();
    }

    animate() {
        requestAnimationFrame(this.animate.bind(this));

        this.cursor.update();

        this.renderer.render(this.scene, this.camera);
    }

    getInitialState() {
        const container = document.querySelector('[data-barba="container"]');
        if (container && container.dataset.city) {
            const cityId = container.dataset.city;
            return { type: 'city', cityId };
        }
        return { type: 'home' };
    }

    onMouseMove(event) {
        this.cursor.onMouseMove(event);

        const rect = this.container.getBoundingClientRect();

        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);
        this.raycaster.far = Math.abs(this.camera.position.z) * 2;
        // Use collision meshes for intersection testing
        const intersects = this.raycaster.intersectObjects(this.collisionMeshes, false);
        let hoveredPostcard = null;

        if (intersects.length > 0) {
            // Get the parent postcard from the collision mesh
            hoveredPostcard = intersects[0].object.userData.parentPostcard;
        }

        // Handle hover state changes
        if (hoveredPostcard !== this.currentlyHoveredPostcard) {
            // Don't unhover if it's the selected postcard
            if (this.currentlyHoveredPostcard && this.currentlyHoveredPostcard !== this.selectedPostcard) {
                this.currentlyHoveredPostcard.unhover();
            }

            // Only hover if it's not already selected
            if (hoveredPostcard && hoveredPostcard !== this.selectedPostcard) {
                hoveredPostcard.hover();
                this.cursor.setHoverState(true);
            } else {
                this.cursor.setHoverState(false);
            }

            this.currentlyHoveredPostcard = hoveredPostcard;
        }

        if (hoveredPostcard === this.selectedPostcard && hoveredPostcard !== null) {
            this.cursor.setHoverState(true);
        } else if (!hoveredPostcard) {
            this.cursor.setHoverState(false);
        }

        if (this.debug) {
            console.log({
                hoveredPostcard: hoveredPostcard?.cityData.id,
                currentHovered: this.currentlyHoveredPostcard?.cityData.id,
                selected: this.selectedPostcard?.cityData.id,
                intersects: intersects.length
            });
        }
    }

    onCityClick(event) {
        const rect = this.container.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = this.raycaster.intersectObjects(this.collisionMeshes, false);

        if (intersects.length > 0) {
            const cityData = intersects[0].object.userData.parentPostcard.cityData;
            if (cityData) {
                event.preventDefault();
                this.openUrl(cityData.detailsUrl);
            }
        }
    }

    openUrl(url) {
        const isExternalLink = url.indexOf('global-opportunity') === -1;
        if (isExternalLink) {
            window.open(url, '_blank');
        } else {
            barba.go(`${url}`, {
                preventUnload: true
            });
        }
    }

    animatePostcardsAppearance() {
        // Create array of postcards with their order info
        const postcardsArray = Array.from(this.postcards.entries()).map(([id, postcard]) => ({
            postcard,
            order: postcard.cityData.postCardOrder
        }));

        // Sort by postCardOrder
        postcardsArray.sort((a, b) => a.order - b.order);

        // Animate each postcard with staggered delay
        postcardsArray.forEach((item, index) => {
            const baseDelay = item.order * 0.1;
            const indexDelay = index * 0.05;
            const isSelected = item.postcard === this.selectedPostcard;
            const delay = isSelected ? 0 : baseDelay + indexDelay;
            item.postcard.appear(delay, isSelected);
        });
    }

    onTouchStart(event) {
        if (event.touches.length === 1) {
            const touch = event.touches[0];
            this.dragStart.x = touch.clientX;
            this.dragStart.y = touch.clientY;
            this.lastDragPosition.x = this.mapGroup.position.x;

            this.velocity.x = 0;
            this.lastTouchTime = Date.now();
            this.lastTouchPosition.x = touch.clientX;
            this.touchMoveDistance = 0;
            this.touchStartTime = Date.now();
            this.isHorizontalDrag = null;

            if (this.inertiaAnimationId) {
                cancelAnimationFrame(this.inertiaAnimationId);
            }
        }
    }

    onTouchMove(event) {
        if (event.touches.length !== 1) return;
        const touch = event.touches[0];

        const deltaX = Math.abs(touch.clientX - this.dragStart.x);
        const deltaY = Math.abs(touch.clientY - this.dragStart.y);

        if (this.isHorizontalDrag === null && (deltaX > 5 || deltaY > 5)) {
            this.isHorizontalDrag = deltaX > deltaY;
            this.isDragging = this.isHorizontalDrag;
        }

        if (!this.isHorizontalDrag) return;

        event.preventDefault();

        const currentTime = Date.now();
        const deltaTime = currentTime - this.lastTouchTime;

        this.touchMoveDistance += Math.abs(touch.clientX - this.lastTouchPosition.x);

        if (deltaTime > 0) {
            this.velocity.x = (touch.clientX - this.lastTouchPosition.x) / deltaTime;
        }

        const movementX = touch.clientX - this.dragStart.x;
        const worldDeltaX = (movementX / (this.container.offsetWidth * this.dragSensitivityFactor)) * (this.mapWidth / this.mapGroup.scale.x);
        const newX = this.lastDragPosition.x + worldDeltaX;

        const isZoomed = this.mapGroup.scale.x >= this.zoomLevels.city;
        const maxOffset = {
            x: this.mapWidth * (isZoomed ? this.panLimits.city : this.panLimits.overview)
        };

        const easeEdge = (value, max) => {
            const excess = Math.abs(value) - max;
            if (excess > 0) {
                const easing = 0.2;
                const sign = value > 0 ? 1 : -1;
                return sign * (max + excess * easing);
            }
            return value;
        };

        this.mapGroup.position.x = easeEdge(newX, maxOffset.x);

        this.lastTouchTime = currentTime;
        this.lastTouchPosition.x = touch.clientX;

        this.updatePopoverPosition();
        this.scheduleIndicatorUpdate();
    }

    updatePopoverPosition() {
        if (this.selectedPostcard) {
            const cardWorldPosition = new THREE.Vector3();
            this.selectedPostcard.group.getWorldPosition(cardWorldPosition);

            const screenPosition = cardWorldPosition.clone();
            screenPosition.project(this.camera);

            const x = (screenPosition.x * 0.5 + 0.5) * this.container.offsetWidth;
            const y = (-screenPosition.y * 0.5 + 0.5) * this.container.offsetHeight;

            const rotation = -parseFloat(this.selectedPostcard.cityData.postCardRotate);
            this.popover.updatePosition({ x, y }, rotation);
        }
    }

    applyInertia() {
        if (!this.isDragging && Math.abs(this.velocity.x) > 0.001) {
            const worldVelocityX = (this.velocity.x * 16 / (this.container.offsetWidth * this.dragSensitivityFactor)) * (this.mapWidth / this.mapGroup.scale.x);

            const damping = 0.95;
            this.velocity.x *= damping;

            const newX = this.mapGroup.position.x + worldVelocityX;

            const isZoomed = this.mapGroup.scale.x >= this.zoomLevels.city;
            const maxOffset = {
                x: this.mapWidth * (isZoomed ? this.panLimits.city : this.panLimits.overview)
            };

            const easeEdge = (value, max) => {
                const excess = Math.abs(value) - max;
                if (excess > 0) {
                    const easing = 0.2;
                    const sign = value > 0 ? 1 : -1;
                    return sign * (max + excess * easing);
                }
                return value;
            };

            this.mapGroup.position.x = easeEdge(newX, maxOffset.x);

            this.updatePopoverPosition();
            this.scheduleIndicatorUpdate();

            this.inertiaAnimationId = requestAnimationFrame(() => this.applyInertia());
        }
    }

    onTouchEnd(event) {
        if (!this.isDragging) return;

        const touchDuration = Date.now() - this.touchStartTime;

        if (this.touchMoveDistance < 10 && touchDuration < 200) {
            const touch = event.changedTouches[0];
            const rect = this.container.getBoundingClientRect();
            this.mouse.x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
            this.mouse.y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;

            this.raycaster.setFromCamera(this.mouse, this.camera);
            const intersects = this.raycaster.intersectObjects(this.collisionMeshes, false);

            if (intersects.length > 0) {
                const cityData = intersects[0].object.userData.parentPostcard.cityData;
                if (cityData) {
                    event.preventDefault();
                    this.openUrl(cityData.detailsUrl);
                }
            }
        }

        this.isDragging = false;
        this.isHorizontalDrag = null;
        this.applyInertia();
    }

    cacheIndicatorDimensions() {
        const maxWidth = 450;
        const calculatedWidth = Math.min(window.innerWidth - 50, maxWidth);

        const thumbWidth = calculatedWidth * 0.4;

        this.indicatorDimensions = {
            trackWidth: calculatedWidth,
            thumbWidth: thumbWidth,
            thumbPercentage: 40,
            maxTravel: calculatedWidth - thumbWidth
        };
    }

    scheduleIndicatorUpdate() {
        if (!this.pendingIndicatorUpdate) {
            this.pendingIndicatorUpdate = true;
            this.indicatorRAF = requestAnimationFrame(() => this.updatePanIndicator());
        }
    }

    updatePanIndicator() {
        this.pendingIndicatorUpdate = false;

        if (!this.isTouchDevice) return;

        const isZoomed = this.mapGroup.scale.x >= this.zoomLevels.city;
        const maxOffset = this.mapWidth * (isZoomed ? this.panLimits.city : this.panLimits.overview);

        if (maxOffset > 0) {
            this.panIndicator.style.opacity = '1';

            const position = (this.mapGroup.position.x + maxOffset) / (maxOffset * 2);
            const clampedPosition = 1 - Math.max(0, Math.min(1, position));
            const availableSpace = this.indicatorDimensions.trackWidth - this.indicatorDimensions.thumbWidth;
            const pixelPosition = clampedPosition * availableSpace;

            this.panIndicatorThumb.style.left = `${pixelPosition}px`;
        } else {
            this.panIndicator.style.opacity = '0';
        }
    }
}
